/**
 * StreakDisplay — Shows user's quest streak + badges
 */
import { useState, useEffect } from 'react';
import { Flame, Trophy, Star, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_ICONS = {
  '3-day-streak': Flame,
  'quest-master': Crown,
  'legendary-foodie': Star,
};

const StreakDisplay = ({ compact = false }) => {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('fq_auth_token');
    fetch(`${API_URL}/api/quests/streak`, {
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStreak(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !streak) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="streak-compact">
        <Flame className="w-4 h-4 text-[#FF6B35]" />
        <span className="text-sm font-bold text-gray-900">{streak.current_streak}</span>
        <span className="text-xs text-gray-500">day streak</span>
        {streak.badges.map(b => (
          <span key={b.id} title={b.label} className="text-sm">{b.emoji}</span>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="streak-display">
      <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-[#FF6B35]" /> Quest Streaks
      </h3>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-gradient-to-br from-[#FFF5F0] to-white rounded-xl p-3 text-center">
          <div className="text-2xl font-black text-[#FF6B35]">{streak.current_streak}</div>
          <div className="text-[10px] text-gray-500 font-medium">Current Streak</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-black text-gray-700">{streak.longest_streak}</div>
          <div className="text-[10px] text-gray-500 font-medium">Best Streak</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-black text-emerald-600">{streak.total_verified}</div>
          <div className="text-[10px] text-gray-500 font-medium">Verified</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <div className="text-2xl font-black text-gray-700">{streak.total_quests}</div>
          <div className="text-[10px] text-gray-500 font-medium">Total Quests</div>
        </div>
      </div>

      {/* Badges */}
      {streak.badges.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-2 font-medium">Badges Earned</p>
          <div className="flex flex-wrap gap-2">
            {streak.badges.map(b => {
              const Icon = BADGE_ICONS[b.id] || Star;
              return (
                <div key={b.id} className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-1.5">
                  <span className="text-sm">{b.emoji}</span>
                  <span className="text-xs font-semibold text-gray-700">{b.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {streak.badges.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">
          Complete 3 verified quests in a row to earn your first badge!
        </p>
      )}
    </div>
  );
};

export default StreakDisplay;
